import clsx from 'clsx';
import { ChevronDown, Plus, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import MutedText from 'components/common/MutedText';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertCollapsible from 'components/HubertUI/HubertCollapsible';
import HubertInput from 'components/HubertUI/HubertInput';
import HubertLabel from 'components/HubertUI/HubertLabel';
import HubertTextArea from 'components/HubertUI/HubertTextArea';
import { useQuestionEditorContext } from 'components/Questionsv2/builder/context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionIsReadonly } from 'rtk/questionEditorSelector';
import { addSlot, updateSlotFilling } from 'rtk/Question.Editor.Slice';
import FieldLabel from './FieldLabel';
import Slot from './Slot';

const GUIDANCE_INFO = (
  <>
    Model-only policy that applies across <strong>all</strong> slots — never shown to the candidate, write it in
    English. Use it for anything a single field&apos;s hint can&apos;t express: how to read ambiguous formats, how to
    treat relative phrasing, or how to tell two slots apart.
  </>
);

const REASKS_INFO = (
  <>
    How many follow-ups Hubert may ask before giving up and moving on with whatever it collected. One follow-up is asked
    per turn, for the first still-missing required field. With four required fields, three re-asks is enough to collect
    them all when the candidate answers one at a time.
  </>
);

// Slot filling: structured-field extraction layered on an Open question. HUBERT-only for now (gated by
// CreateOpen). The candidate still answers in free text — these define what gets pulled out of it.
const SlotFillingContainer = () => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const slotFilling = useAppSelector((state) => state.questionEditor.byId[questionId].slot_filling);
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const [open, setOpen] = useState(false);

  if (isReadOnly) {
    return null;
  }

  const slots = slotFilling?.slots ?? [];

  // Slot ids are the extraction field names and the storage keys, so empty/duplicate ids silently
  // break extraction (two slots sharing an id collapse into a single value).
  const ids = slots.map((slot) => slot.id.trim());
  const hasEmptyId = ids.some((id) => id.length === 0);
  const duplicateIds = [...new Set(ids.filter((id, i) => id.length > 0 && ids.indexOf(id) !== i))];

  return (
    <HubertCollapsible className="p-1" open={open} onOpenChange={setOpen}>
      <div className="mb-1 flex flex-row items-center justify-between">
        <HubertCollapsible.Trigger className="flex cursor-pointer flex-row items-center gap-2">
          <ChevronDown
            size={16}
            className={`cursor-pointer transition-transform duration-200 ${open ? 'rotate-180' : 'rotate-0'}`}
          />
          <HubertLabel className="cursor-pointer">
            Slot filling
            <MutedText className="cursor-pointer" text=" - extract structured fields from the open answer" />
          </HubertLabel>
          {slots.length > 0 && (
            <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
              {slots.length}
            </span>
          )}
        </HubertCollapsible.Trigger>

        {slots.length > 0 && (
          <HubertButton size="sm" iconLeft={<Plus size={14} />} onClick={() => dispatch(addSlot({ questionId }))}>
            Add slot
          </HubertButton>
        )}
      </div>

      <HubertCollapsible.Content className="flex flex-col gap-3">
        {slots.length === 0 ? (
          <div className={clsx('flex flex-col items-start gap-2', 'rounded-md border border-dashed', 'p-4')}>
            <MutedText
              className="text-xs"
              text="The candidate still answers this question in free text. Slots define the structured values Hubert extracts from that answer — and Hubert asks a follow-up for any required value it couldn't find."
            />
            <HubertButton size="sm" iconLeft={<Plus size={14} />} onClick={() => dispatch(addSlot({ questionId }))}>
              Add first slot
            </HubertButton>
          </div>
        ) : (
          // Keyed by position, NOT by slot.id — the id is editable, and keying on it remounts the row
          // on every keystroke, which drops input focus.
          slots.map((slot, index) => <Slot key={`slot-${index}`} slot={slot} slotIndex={index} />)
        )}

        {(hasEmptyId || duplicateIds.length > 0) && (
          <div
            className={clsx(
              'flex flex-row items-start gap-2',
              'border-danger-200 bg-danger-50 rounded-md border',
              'text-danger-700 p-3 text-sm',
            )}
          >
            <TriangleAlert size={16} className="mt-0.5 shrink-0" />
            <div className="flex flex-col gap-0.5">
              {hasEmptyId && <span>Every slot needs a field id (e.g. date_of_birth).</span>}
              {duplicateIds.length > 0 && <span>Duplicate field ids: {duplicateIds.join(', ')}.</span>}
            </div>
          </div>
        )}

        {slots.length > 0 && (
          <div className="flex flex-col gap-3 border-t pt-3">
            <div className="flex flex-col gap-1">
              <FieldLabel label="Extraction guidance" hint="optional, model-only, English" info={GUIDANCE_INFO} />
              <HubertTextArea
                rows={3}
                placeholder="e.g. Identity-confirmation question. Read dates in day/month/year order. Never guess a date of birth from age."
                value={slotFilling?.extraction_guidance ?? ''}
                onChange={(e) =>
                  dispatch(updateSlotFilling({ questionId, patch: { extraction_guidance: e.target.value } }))
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <FieldLabel label="Max re-asks" hint="default 3" info={REASKS_INFO} />
              <HubertInput
                type="number"
                className="w-24"
                value={slotFilling?.max_reasks ?? ''}
                onChange={(e) =>
                  dispatch(
                    updateSlotFilling({
                      questionId,
                      patch: { max_reasks: e.target.value === '' ? undefined : Number(e.target.value) },
                    }),
                  )
                }
              />
            </div>
          </div>
        )}
      </HubertCollapsible.Content>
    </HubertCollapsible>
  );
};

export default SlotFillingContainer;
